import { useEffect, useState } from "react";
import { Link2, Loader2, CheckCircle2, AlertTriangle, XCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, FILES_BASE } from "@/lib/api";
import MetaAutoConnect from "@/components/MetaAutoConnect";

export const META_LABEL = {
  setup_required: ["SETUP REQUIRED", "bg-amber-500/10 text-amber-700 border-amber-500/30"],
  not_connected: ["NOT CONNECTED", "bg-slate-900 text-slate-400 border-slate-800"],
  expired: ["EXPIRED - RECONNECT", "bg-rose-500/10 text-rose-400 border-rose-400/30"],
  connected: ["CONNECTED", "bg-emerald-500/10 text-emerald-500 border-emerald-500/30"],
};

export function useMeta() {
  const [s, setS] = useState(null);
  const load = () => api.get("/meta/status").then((r) => setS(r.data)).catch(() => setS(null));
  useEffect(() => { load(); }, []);
  return [s, load];
}

export function MetaStateBadge({ state }) {
  const [l, c] = META_LABEL[state] || META_LABEL.not_connected;
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border ${c}`} data-testid="meta-state-badge">{l}</span>;
}

function AssetPicker({ onDone }) {
  return <MetaAutoConnect onDone={onDone} />;
}

export function MetaConnectCard({ meta, reload, canWrite }) {
  const [pick, setPick] = useState(false);
  const autoOpen = meta?.state === "connected" && !meta?.assets_ready;
  const show = pick || autoOpen;
  if (!meta) return null;
  const con = meta.connection;
  const connect = async () => { try { window.location.assign((await api.get("/meta/connect")).data.url); } catch (e) { toast.error(errMsg(e)); } };
  const disconnect = async () => { await api.post("/meta/disconnect"); toast.success("Meta disconnected"); reload(); };
  return (
    <div className="lx-card mb-6" data-testid="meta-connect-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-semibold">Meta (Facebook & Instagram) Ads <MetaStateBadge state={meta.state} /></div>
          <p className="text-xs text-slate-400 mt-1">
            {meta.state === "connected" && meta.assets_ready && `Ad account ${con.ad_account_name} · Page ${con.page_name}${con.instagram_username ? ` · @${con.instagram_username}` : ""}${con.pixel_name ? ` · Pixel ${con.pixel_name}` : ""}`}
            {meta.state === "connected" && !meta.assets_ready && "Connected. We are reading your Instagram, Page and ad account. Please confirm below."}
            {meta.state === "not_connected" && "Connect your own Meta ad account. You keep ownership and billing; we never ask for your password."}
            {meta.state === "expired" && "Meta access expired. Reconnect to keep publishing and syncing."}
            {meta.state === "action_required" && <span className="text-rose-500" data-testid="meta-action-required">Meta did not give a valid User access token (token type: {con.token_type}). Not connected. Press Connect My Meta Account again after fixing the Login Configuration.</span>}
            {meta.config_problems?.length > 0 && <span className="block text-rose-500" data-testid="meta-config-problems">Setup problem: {meta.config_problems.join("; ")}</span>}
            {meta.state === "setup_required" && "Real publishing to Meta is not switched on yet. Campaigns can be drafted and simulated meanwhile."}
          </p>
        </div>
        {canWrite && meta.configured && (
          <div className="flex gap-2">
            {meta.state !== "connected" && <Button onClick={connect} data-testid="meta-connect-button"><Link2 className="h-4 w-4 mr-1" />Connect My Meta Account</Button>}
            {meta.state === "connected" && <Button variant="ghost" onClick={connect} data-testid="meta-reconnect-button">Reconnect</Button>}
            {meta.state === "connected" && meta.assets_ready && <Button variant="secondary" onClick={() => setPick(!pick)} data-testid="meta-choose-assets-button">Change accounts</Button>}
            {con && <Button variant="ghost" onClick={disconnect} data-testid="meta-disconnect-button">Disconnect</Button>}
          </div>
        )}
      </div>
      {meta.state === "setup_required" && (
        <ol className="list-decimal pl-5 mt-3 space-y-1 text-xs text-slate-300 break-words [overflow-wrap:anywhere]" data-testid="meta-setup-steps">{meta.setup_steps.map((s) => <li key={s}>{s}</li>)}</ol>
      )}
      {show && <AssetPicker onDone={() => { setPick(false); reload(); }} />}
    </div>
  );
}

export function CampaignMetaBadge({ c }) {
  const m = c.meta;
  if (m?.campaign_id) {
    const live = m.status === "ACTIVE";
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-mono border ${live ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" : "bg-cyan-500/10 text-cyan-700 border-cyan-600/30"}`} title={`Meta ID ${m.campaign_id}`} data-testid={`campaign-meta-badge-${c.id}`}>
      {live ? `ON META: ${m.effective_status || "ACTIVE"}` : "PAUSED ON META"}</span>;
  }
  if (String(c.external_id || "").startsWith("MOCK")) return <span className="lx-mock-badge" data-testid={`campaign-meta-badge-${c.id}`}>SIMULATED</span>;
  return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-mono border border-slate-800 text-slate-400" data-testid={`campaign-meta-badge-${c.id}`}>DRAFT</span>;
}

const today = () => new Date().toISOString().slice(0, 10);

export function MetaPublishDialog({ campaign, onClose, onDone }) {
  const [f, setF] = useState({ creative_id: "", headline: campaign.name.slice(0, 40), message: "", landing_url: "", lead_form_id: "", city: "", radius_km: 25,
    daily_budget: campaign.daily_budget || 1000, start_date: campaign.start_date || today(), end_date: campaign.end_date || "" });
  const [creatives, setCreatives] = useState([]);
  const [forms, setForms] = useState([]);
  const [checks, setChecks] = useState(null);
  const [busy, setBusy] = useState(false);
  const isLeads = (campaign.objective || "Leads") === "Leads";
  useEffect(() => {
    Promise.all([api.get("/creatives"), api.get("/assets", { params: { project_id: campaign.project_id || undefined } })]).then(([c, a]) =>
      setCreatives([...c.data.filter((x) => x.file_url && x.status === "completed" && x.type !== "greeting_bg"), ...a.data.filter((x) => ["building", "template"].includes(x.kind))]));
    if (isLeads) api.get("/meta/lead-forms").then((r) => setForms(r.data)).catch(() => setForms([]));
  }, [isLeads, campaign.project_id]);
  const body = () => ({ ...f, daily_budget: Number(f.daily_budget), radius_km: Number(f.radius_km), end_date: f.end_date || null, lead_form_id: f.lead_form_id || null, landing_url: f.landing_url || null });
  const check = async () => { try { setChecks((await api.post(`/campaigns/${campaign.id}/meta/compliance`, body())).data); } catch (e) { toast.error(errMsg(e)); } };
  const send = async () => {
    setBusy(true);
    try { await api.post(`/campaigns/${campaign.id}/meta/publish`, body()); toast.success("Created on Meta as PAUSED. Review it, then press Publish."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => { setF({ ...f, [k]: e.target ? e.target.value : e }); setChecks(null); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-slate-800" data-testid="meta-publish-dialog">
        <DialogHeader><DialogTitle>Send "{campaign.name}" to Meta</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Objective: {campaign.objective || "Leads"}. The campaign is created on your ad account as <b>PAUSED</b>; no money is spent until you press Publish.</p>
        <div className="space-y-1"><Label className="text-xs">1. Pick an approved image from Creative Studio</Label>
          <div className="flex gap-2 overflow-x-auto pb-2">{creatives.map((c) => (
            <button key={c.id} onClick={() => set("creative_id")(c.id)} data-testid={`meta-creative-${c.id}`} className={`shrink-0 rounded-lg border-2 overflow-hidden ${f.creative_id === c.id ? "border-emerald-500" : "border-transparent"}`}>
              <img src={`${FILES_BASE}${c.file_url}`} alt={c.title} className="h-20 w-20 object-cover" /></button>))}
            {!creatives.length && <span className="text-xs text-slate-500">No images yet. Create one in Creative Studio first.</span>}</div></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">2. Headline (max 40)</Label><Input value={f.headline} onChange={set("headline")} data-testid="meta-headline-input" /></div>
          {isLeads ? (
            <div className="space-y-1"><Label className="text-xs">Lead form (Meta Instant Form)</Label>
              <Select value={f.lead_form_id} onValueChange={set("lead_form_id")}><SelectTrigger data-testid="meta-leadform-select"><SelectValue placeholder={forms.length ? "Choose" : "No forms found on your Page"} /></SelectTrigger>
                <SelectContent>{forms.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
          ) : <div className="space-y-1"><Label className="text-xs">Landing page (https://)</Label><Input value={f.landing_url} onChange={set("landing_url")} placeholder="https://yourproject.com" data-testid="meta-landing-input" /></div>}
          <div className="sm:col-span-2 space-y-1"><Label className="text-xs">3. Main ad text</Label><Textarea rows={3} value={f.message} onChange={set("message")} data-testid="meta-message-input" /></div>
          <div className="space-y-1"><Label className="text-xs">4. City</Label><Input value={f.city} onChange={set("city")} placeholder="Pune" data-testid="meta-city-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Radius around city (km, min 25)</Label><Input type="number" value={f.radius_km} onChange={set("radius_km")} data-testid="meta-radius-input" /></div>
          <div className="space-y-1"><Label className="text-xs">5. Daily budget (INR)</Label><Input type="number" value={f.daily_budget} onChange={set("daily_budget")} data-testid="meta-budget-input" /></div>
          <div className="grid grid-cols-2 gap-2"><div className="space-y-1"><Label className="text-xs">Start</Label><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="meta-start-input" /></div>
            <div className="space-y-1"><Label className="text-xs">End (optional)</Label><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="meta-end-input" /></div></div>
        </div>
        {checks && (
          <ul className="space-y-1 text-xs border-t border-slate-800 pt-3" data-testid="meta-compliance-list">{checks.checks.map((c) => (
            <li key={c.check} className="flex gap-2">{c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <XCircle className="h-4 w-4 text-rose-400 shrink-0" />}<span><b>{c.check}</b> · {c.detail}</span></li>))}</ul>
        )}
        <div className="flex flex-wrap gap-2 justify-end">
          <Button variant="secondary" onClick={check} data-testid="meta-compliance-button"><AlertTriangle className="h-4 w-4 mr-1" />6. Check policy & compliance</Button>
          <Button onClick={send} disabled={!checks?.passed || busy} data-testid="meta-send-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}Create on Meta (paused)</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MetaGoLiveDialog({ campaign, onClose, onDone }) {
  const [ok, setOk] = useState(false);
  const go = async () => {
    try { await api.post(`/campaigns/${campaign.id}/meta/status`, { status: "ACTIVE", confirm_spend: true }); toast.success("Published. Meta will review the ad before delivery."); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const s = campaign.meta?.setup || {};
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-800" data-testid="meta-golive-dialog">
        <DialogHeader><DialogTitle>Approve & publish on Meta</DialogTitle></DialogHeader>
        <div className="text-sm space-y-1 text-slate-300">
          <div>Daily budget: <b>INR {Number(s.daily_budget || 0).toLocaleString("en-IN")}</b></div>
          <div>Dates: {s.start_date} → {s.end_date || "until you pause it"}</div>
          {s.end_date && <div>Estimated maximum spend: <b>INR {(Number(s.daily_budget) * (Math.round((new Date(s.end_date) - new Date(s.start_date)) / 864e5) + 1)).toLocaleString("en-IN")}</b></div>}
          <p className="text-xs text-slate-400 pt-2">Spend is charged by Meta to your own ad account's payment method, not by LaunchX360.</p>
        </div>
        <label className="flex items-start gap-2 text-sm"><Checkbox checked={ok} onCheckedChange={setOk} data-testid="meta-confirm-spend-checkbox" />I approve real ad spend on my Meta ad account.</label>
        <Button disabled={!ok} onClick={go} data-testid="meta-golive-button">Publish now</Button>
      </DialogContent>
    </Dialog>
  );
}
